import { defineStore } from 'pinia';
import customSort, { type ICustomSorting } from '~/composables/custom-sort';
import type { IDropdownOption } from '~/shared/interfaces/ui/dropdown';
import type {
  ISearchProduct,
  ISearchProductResult,
  IBrandFilter,
  ISearchProductWarehouse,
  TAnalogsListSortingKeys,
  TSearchFilterTypes,
  TSearchSwitchersTypes,
  TWarehousesListSortingTypes,
} from "~/shared/interfaces/search/products";
import type { ICity } from '~/shared/interfaces/main';
import type { IImageSlide } from "~/shared/interfaces/ui/sliders";
import type { IOptionWithCategory } from '~/shared/interfaces/ui/form/formInputs'
import type { TSuccessModal } from "~/shared/interfaces/search/product";
import type { IProductQuery, IProductRequestItem } from '~/shared/interfaces/search/resultEmpty';
import useAccountStore from '../account';
import customFetch from '~/composables/custom-fetch';
import apiConfig from '~/shared/config/api';

const SUBMISSION_TIMEOUT_HRS = 4;
const FORM_ALREADY_SUBMITTED_STATUS_CODE = 202;
const PRODUCT_REQUEST_STORAGE_KEY = "requested-products";

interface ISearchInitialData {
  citiesList: ICity[]
  brandsList: IBrandFilter[]
  originalProduct: ISearchProduct | null
  productAnalogsList: ISearchProduct[]
}

interface ISearchState extends ISearchInitialData {
  isSortModal: boolean
  isFilterModal: boolean
  isSuccessModal: boolean
  isBuyOneClickModal: boolean
  isRememberMeModal: boolean
  isProductRequestModal: boolean
  isProductRequestSent: boolean
  rememberMeModalEmail: string
  successModalType: TSuccessModal
  selectedFilters: Record<TSearchFilterTypes, IDropdownOption[]>
  selectedSwitchersFilters: Record<TSearchSwitchersTypes, boolean>
  analogsListSorting: Record<TAnalogsListSortingKeys, boolean | null>
  warehousesListSorting: Record<TWarehousesListSortingTypes, boolean | null>
  searchProductSlides: IImageSlide[]
  isShowProductsInCity: boolean
  hiddenProductsCity: string
}

type TSelectedFiltersGetter = [TSearchFilterTypes, IDropdownOption[]][];

const useSearchStore = defineStore('search', {
  state: (): ISearchState => ({
    searchProductSlides: [],
    isSortModal: false,
    isFilterModal: false,
    isSuccessModal: false,
    isBuyOneClickModal: false,
    isRememberMeModal: false,
    isProductRequestModal: false,
    isProductRequestSent: false,
    rememberMeModalEmail: '',
    successModalType: 'oneClick',
    originalProduct: null,
    productAnalogsList: [],
    brandsList: [],
    citiesList: [],
    selectedFilters: {
      brands: [],
      cities: [],
      specialOffers: [],
    },
    selectedSwitchersFilters: {
      hasPhoto: false,
      inStock: false,
    },
    analogsListSorting: {
      sortByBrand: null,
      sortByArticle: null,
      sortByName: null,
    },
    warehousesListSorting: {
      sortByPrice: null,
      sortByCity: null,
      sortByDeliveryDate: null,
    },
    isShowProductsInCity: true,
    hiddenProductsCity: 'all',
  }),
  getters: {
    getSpecaialOffersState() {

    },
    getSelectedFiltersList(): TSelectedFiltersGetter {
      return Object.entries(this.selectedFilters) as TSelectedFiltersGetter;
    },
    getSelectedFiltersLength(): number {
      return Object.values(this.selectedFilters).flat(1).length
    },
    getSearchProductData(): ISearchProductResult {
      const { brands, cities, specialOffers } = this.selectedFilters;
      const { sortByPrice, sortByDeliveryDate, sortByCity } = this.warehousesListSorting;

      let originalProduct = this.originalProduct;
      let analogsList = this.productAnalogsList;

      if (
        brands.length ||
        cities.length ||
        specialOffers.length ||

        this.selectedSwitchersFilters.hasPhoto ||
        this.selectedSwitchersFilters.inStock ||

        sortByPrice !== null ||
        sortByDeliveryDate !== null ||
        sortByCity !== null ||
        this.hiddenProductsCity
      ) {
        const productsList: ISearchProduct[] = JSON.parse(JSON.stringify([originalProduct, ...analogsList]));
        analogsList = productsList.reduce<ISearchProduct[]>((acc, product, i) => {
          const isOriginalProduct = i == 0;
          let hasInList = true;

          if (product) {
            if (brands.length) {
              hasInList = Boolean(brands.find(brand => brand.id == product.brand.id))
            }

            if (hasInList && (cities.length || specialOffers.length || this.selectedSwitchersFilters.hasPhoto || this.selectedSwitchersFilters.inStock || this.hiddenProductsCity)) {
              product.warehouses = product.warehouses.filter(warehouse => {
                let filterHasInWarehouse = true;

                if (cities.length) {
                  filterHasInWarehouse = Boolean(cities.find(city => city.id == warehouse.city.id));
                }
                if (specialOffers.length && filterHasInWarehouse) {
                  filterHasInWarehouse = Boolean(specialOffers.find(specFilter => {
                    return warehouse[specFilter.id as keyof ISearchProductWarehouse]
                  }));
                }
                if (this.selectedSwitchersFilters.hasPhoto && filterHasInWarehouse) {
                  filterHasInWarehouse = Boolean(warehouse.photos.length);
                }
                if (this.selectedSwitchersFilters.inStock && filterHasInWarehouse) {
                  filterHasInWarehouse = !Boolean(warehouse.dateDelivery);
                }

                if(filterHasInWarehouse) {
                  filterHasInWarehouse = warehouse.isShow
                }

                if(!this.isShowProductsInCity && warehouse.city.slug === this.hiddenProductsCity && !warehouse.isShow) {
                  filterHasInWarehouse = true
                }

                return filterHasInWarehouse;
              })

              if (!product.warehouses.length) {
                hasInList = false;
              }
            }

            if (hasInList) {
              if (
                sortByPrice != null ||
                sortByCity != null ||
                sortByDeliveryDate != null
              ) {
                const config: ICustomSorting<ISearchProductWarehouse> = {
                  arr: product.warehouses,
                  sortingType: (sortByPrice != null && 'number') || (sortByCity != null && 'alphabet') || (sortByDeliveryDate != null && 'date') || 'number',
                  field: (sortByPrice != null && 'price') || (sortByCity != null && 'city.name') || (sortByDeliveryDate != null && 'dateDelivery') || '',
                  direction: (sortByPrice || sortByCity || sortByDeliveryDate) ? 'asc' : 'desc',
                }
                customSort(config);
              }

              if (isOriginalProduct) {
                originalProduct = product
              } else {
                acc.push(product);
              }
            } else if (isOriginalProduct) {
              originalProduct = null;
            }
          }

          return acc;
        }, []);
      }

      if (analogsList.length) {
        const { sortByBrand, sortByArticle, sortByName } = this.analogsListSorting;

        if (sortByBrand != null || sortByArticle != null || sortByName != null) {
          const config: ICustomSorting<ISearchProduct> = {
            arr: analogsList,
            sortingType: 'alphabet',
            field: (sortByBrand != null && 'brand.name') || (sortByArticle != null && 'article') || (sortByName != null && 'title') || '',
            direction: (sortByBrand || sortByArticle || sortByName) ? 'asc' : 'desc',
          }
          customSort(config);
        }
      }

      return {
        cityFlag: this.isShowProductsInCity,
        original: originalProduct,
        analogs: analogsList,
      };
    },
  },
  actions: {
    setIsShowProducts(flag: boolean, hiddenProductsCity: string) {
      this.isShowProductsInCity = flag;
      if(!this.isShowProductsInCity) this.hiddenProductsCity = hiddenProductsCity
    },
    setSwitchersFiltersActivity(type: TSearchSwitchersTypes, isActive: boolean) {
      this.selectedSwitchersFilters[type] = isActive;
    },
    setSearchFilters(filters: IDropdownOption[], category: TSearchFilterTypes) {
      this.selectedFilters[category] = filters;
    },
    addSearchFilter(filter: IOptionWithCategory) {
      this.selectedFilters[filter.category].push(filter);
    },
    removeSearchFilter(filterOnRemove: IOptionWithCategory) {
      this.selectedFilters[filterOnRemove.category] = this.selectedFilters[filterOnRemove.category].filter((filter: IDropdownOption) => filter.id != filterOnRemove.id)
    },
    clearAllSelectedFilters() {
      this.selectedFilters = {
        brands: [],
        cities: [],
        specialOffers: [],
      }

      this.selectedSwitchersFilters = {
        inStock: false,
        hasPhoto: false
      }
    },
    setAnalogsSorting(sortingType: TAnalogsListSortingKeys) {
      for (const key in this.analogsListSorting) {
        if (key != sortingType) {
          this.analogsListSorting[key as TAnalogsListSortingKeys] = null;
        }
      }

      this.analogsListSorting[sortingType] = !this.analogsListSorting[sortingType];
    },
    setWarehousesSorting(sortingType: TWarehousesListSortingTypes) {
      for (const key in this.warehousesListSorting) {
        if (sortingType != key) {
          this.warehousesListSorting[key as TWarehousesListSortingTypes] = null;
        }
      }

      this.warehousesListSorting[sortingType] = !this.warehousesListSorting[sortingType];
    },
    clearSorting() {
      this.analogsListSorting = {
        sortByBrand: null,
        sortByArticle: null,
        sortByName: null,
      }

      this.warehousesListSorting = {
        sortByPrice: null,
        sortByCity: null,
        sortByDeliveryDate: null,
      }
    },
    initSearchProductsList({
      productAnalogsList,
      originalProduct,
      citiesList,
      brandsList,
    }: ISearchInitialData) {
      this.productAnalogsList = productAnalogsList;
      this.originalProduct = originalProduct;
      this.citiesList = citiesList;
      this.brandsList = brandsList;
    },
    setSortModalState(state: boolean) {
      this.isSortModal = state
    },
    setFilterModalState(state: boolean) {
      this.isFilterModal = state
    },
    clearSearchProductSlides() {
      this.searchProductSlides = []
    },
    setSearchProductSlides(slides: IImageSlide[]) {
      this.searchProductSlides = slides
    },
    sortFilters(filterKey: 'brandsList' | 'citiesList') {
      const selectedFilterKey = filterKey == 'brandsList' ? 'brands' : 'cities';

      this[filterKey].sort((a, b) => {
        if (
          this.selectedFilters[selectedFilterKey].find(filter => filter.id == a.id) &&
          !this.selectedFilters[selectedFilterKey].find(filter => filter.id == b.id)
        ) {
          return -1
        }

        return 0
      })
    },
    setOneClickModalState(state: boolean) {
      this.isBuyOneClickModal = state
    },
    setRememberMeModalState(state: boolean) {
      this.isRememberMeModal = state
    },
    setRememberMeModalEmail(email: string) {
      this.rememberMeModalEmail = email
    },
    setSuccessModalState(state: boolean) {
      this.isSuccessModal = state
    },
    setSuccessModalType(type: TSuccessModal) {
      this.successModalType = type
    },
    setProductRequestModalState(state: boolean) {
      this.isProductRequestModal = state;
    },
    setProductRequestSentState(state: boolean) {
      this.isProductRequestSent = state;
    },
    async handleProductRequestSentState(article: string, brand: string) {
      const isGuest = useAccountStore().userRole === "guest";

      if (!isGuest) {
        const queryParams: IProductQuery = {
          article,
          ...(brand && { brand }),
        };

        const { status } = await customFetch(
          apiConfig.search.getProductRequestStatus(queryParams)
        );

        this.setProductRequestSentState(
          status === FORM_ALREADY_SUBMITTED_STATUS_CODE
        );
        return;
      }

      const requestsData = localStorage.getItem(PRODUCT_REQUEST_STORAGE_KEY);
      if (!requestsData || requestsData === '[]') {
        this.setProductRequestSentState(false);
        return;
      }

      const requestDataParsed: IProductRequestItem[] = JSON.parse(requestsData);
      const dataItem = requestDataParsed.find(
        (el) => el.article === article && el.brand === brand
      );

      if (!dataItem) {
        this.setProductRequestSentState(false);
        return;
      }

      const hasTimeoutPassed = this.hasSubmitTimeoutPassed(
        dataItem.timestamp,
        SUBMISSION_TIMEOUT_HRS * 1000 * 60 * 60
      );

      this.setProductRequestSentState(!hasTimeoutPassed);

      if (hasTimeoutPassed) {
        const updatedProductsList = requestDataParsed.filter(
          (el) => !(el.article === article && el.brand === brand)
        );

        localStorage.setItem(
          PRODUCT_REQUEST_STORAGE_KEY,
          JSON.stringify(updatedProductsList)
        );
      }
    },
    setSubmissionTimestamp(timestamp: number, article: string, brand: string) {
      let dataToPushToLocalStorage: IProductRequestItem[] = [];

      const currentData = localStorage.getItem(PRODUCT_REQUEST_STORAGE_KEY);
      const requestItem = { timestamp, article, brand };

      if (!currentData || !currentData.length) {
        dataToPushToLocalStorage.push(requestItem);
      } else {
        dataToPushToLocalStorage = [...JSON.parse(currentData), requestItem];
      }

      localStorage.setItem(
        PRODUCT_REQUEST_STORAGE_KEY,
        JSON.stringify(dataToPushToLocalStorage)
      );
    },
    hasSubmitTimeoutPassed(timestampMs: number, timeoutMs: number) {
      const now = Date.now();
      return now - timestampMs > timeoutMs;
    },
  },
});

export default useSearchStore;
